import {Timestamp} from '@angular/fire/firestore';
import {DEFAULT_STORE} from '../../../shared/shared-models/store/store';
import {_USER_DEFAULT} from '../../user-access/models';
import {NotificationType, NotificationTypeEnum} from './notification-types.enum';
import {IAutoOrderNotificationItemError} from './notification-auto-order-item-error.model';

//#region INTERFACE INotificationRead
/**
 * ### INotificationRead
 * Represents a notification read event.
 *
 * @interface INotificationRead
 * @extends Object
 * @description This interface tracks when and by whom a notification was read.
 *
 * @example
 * const readEvent: INotificationRead = {
 *   userId: 'user_123',
 *   timestamp: someTimestamp
 * };
 */
export interface INotificationRead extends Object {
  /**
   * The timestamp of when the notification was read.
   *
   * @type {Timestamp}
   */
  timestamp: Timestamp;

  /**
   * The ID of the user who read the notification.
   *
   * @type {string}
   * @example "user_123"
   */
  userId: string;
}

//#endregion

//#region INTERFACE INotification
/**
 * ### INotification
 * Represents a notification object with all related metadata.
 *
 * @interface INotification
 * @extends Object
 * @description Contains details about a notification event including its type,
 * user association, status, timestamps, and optional read-tracking state.
 *
 * @example
 * const notification: INotification = {
 *   event: "OrderPlaced",
 *   notificationId: "notif_001",
 *   response: "Order has been placed successfully",
 *   storeId: "store_123",
 *   success: true,
 *   timestamp: someTimestamp,
 *   type: "AUTO_ORDERS",
 *   userId: "user_123",
 *   isNotificationRead: true,
 *   read: [{ userId: "user_123", timestamp: someTimestamp }],
 *   reverted: false
 * };
 */
export interface INotification extends Object {
  /**
   * The event name associated with the notification.
   *
   * @type {string}
   * @example "OrderPlaced"
   */
  event: string;

  /**
   * The unique identifier for the notification.
   *
   * @type {string}
   * @example "notif_001"
   */
  notificationId: string;

  /**
   * The response message related to the event.
   *
   * @type {string}
   * @example "Order has been placed successfully"
   */
  response: string;

  /**
   * The store ID where the event occurred.
   *
   * @type {string}
   * @example "store_123"
   */
  storeId: string;

  /**
   * Indicates whether the event was successful.
   *
   * @type {boolean}
   * @example true
   */
  success: boolean;

  /**
   * Timestamp of when the notification was created.
   *
   * @type {Timestamp}
   */
  timestamp: Timestamp;

  /**
   * The type of notification.
   *
   * @type {NotificationType}
   * @example "AUTO_ORDERS"
   */
  type: NotificationType;

  /**
   * The ID of the user associated with the event.
   *
   * @type {string}
   * @example "user_123"
   */
  userId: string;

  /**
   * Indicates whether the notification has been read by the current user.
   *
   * @type {boolean}
   * @optional
   */
  isNotificationRead?: boolean;

  /**
   * Per-item errors for failed auto-order order responses.
   *
   * @type {Record<string, IAutoOrderNotificationItemError> | null}
   * @optional
   */
  itemErrors?: Record<string, IAutoOrderNotificationItemError> | null;

  newStockItem?: boolean;

  /**
   * An optional array of read events related to the notification.
   *
   * @type {INotificationRead[]}
   * @optional
   */
  read?: INotificationRead[];

  responseType?: NotificationResponseTypeEnum;

  /**
   * Indicates whether the notification has been retried.
   *
   * @type {boolean}
   * @optional
   */
  retried?: boolean;

  /**
   * Indicates whether the notification has been reverted.
   *
   * @type {boolean}
   * @optional
   */
  reverted?: boolean;
}

//#endregion

//#region CONST DEFAULT_LOADING_NOTIFICATION
/**
 * ### DEFAULT_LOADING_NOTIFICATION
 * Constant representing a default loading-state notification.
 *
 * This notification is used as a placeholder when loading notifications from the backend.
 *
 * @constant
 * @type {INotification}
 * @example
 * ```ts
 * const loading = DEFAULT_LOADING_NOTIFICATION;
 * console.info(loading.type); // "LOADING"
 * ```
 */
export const DEFAULT_LOADING_NOTIFICATION: INotification = {
  event: 'Loading',
  notificationId: 'loading',
  response: 'Loading notifications...',
  storeId: DEFAULT_STORE.storeId,
  success: true,
  timestamp: Timestamp.now(),
  type: NotificationTypeEnum.LOADING,
  userId: _USER_DEFAULT.id,
  read: [{userId: _USER_DEFAULT.id, timestamp: Timestamp.now()}],
  isNotificationRead: true,
};
//#endregion

export enum NotificationResponseTypeEnum {
  newItemExists = 'newItemExists',
  noError = 'noError',
  duplicateBarcode = 'duplicateBarcode',
  unknownError = 'unknownError',
  orderResponse = 'ORDER_RESPONSE',
}
